// src/Payroll_Management/PayRoll_Report/EmployeePFList.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  FileSpreadsheet,
  Printer,
  ChevronDown,
  ChevronUp,
  Hash,
  Type,
  Landmark,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const monthOptions = [
  { label: "Select", value: "Select" },
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

// Value -> month name (ASP passes SelectedItem.Text)
const monthNameByValue = {
  Select: "Select",
  "1": "January",
  "2": "February",
  "3": "March",
  "4": "April",
  "5": "May",
  "6": "June",
  "7": "July",
  "8": "August",
  "9": "September",
  "10": "October",
  "11": "November",
  "12": "December",
};

function normalizeSelect(v) {
  const t = (v ?? "").toString().trim();
  return !t || t === "Select" ? "Select" : t;
}

const EmployeePFList = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "8"; // login branch
  const branchType = localStorage.getItem("branchType") || "HO"; // HO / etc

  const screenBadge = useMemo(() => "INFRA SCREEN : C-58", []);

  // UI state
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [metaLoaded, setMetaLoaded] = useState(false);

  // meta
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [years, setYears] = useState([]);

  // filters (ASP defaults: current month/year, branch=session branch)
  const [year, setYear] = useState("Select");
  const [month, setMonth] = useState("Select"); // numeric string "1".."12"
  const [branch, setBranch] = useState("Select"); // branchId
  const [departmentId, setDepartmentId] = useState("Select");
  const [paymode, setPaymode] = useState("Select"); // Cash/Bank/Select
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  // autocomplete
  const [codeSuggestions, setCodeSuggestions] = useState([]);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [codeOpen, setCodeOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);

  // list
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const subtleBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white bg-white/5 hover:bg-white/10"
      : "border-gray-200 text-gray-800 bg-gray-50 hover:bg-gray-100");

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || !String(branchType).trim()) {
      toast.warn("Missing login branch/branchtype in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-pf-list/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setDepartments(res.data.departments || []);
        setYears(res.data.years || []);

        // defaults
        const defBranch = res.data.defaultBranchId ? String(res.data.defaultBranchId) : "Select";
        const defMonth = String(res.data.defaultMonth || "Select");
        const defYear = String(res.data.defaultYear || "Select");

        setBranch(defBranch);
        setMonth(defMonth);
        setYear(defYear);
        setDepartmentId("Select");
        setPaymode("Select");

        setMetaLoaded(true);

        // initial load like ASP fillgrid()
        setPage(1);
        await fetchList(1, {
          branch: defBranch,
          month: defMonth,
          year: defYear,
          departmentId: "Select",
          empCode: "",
          empName: "",
          paymode: "Select",
        });
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed.");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page, overrideFilters = null) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    const f = overrideFilters || {
      branch,
      month,
      year,
      departmentId,
      empCode,
      empName,
      paymode,
    };

    const monthName = monthNameByValue[normalizeSelect(f.month)] || "Select";

    // Skip API call if mandatory fields are still "Select"
    if (monthName === "Select" || normalizeSelect(f.year) === "Select") {
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-pf-list/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId: normalizeSelect(f.branch),
          monthName: monthName,
          year: normalizeSelect(f.year),
          departmentId: normalizeSelect(f.departmentId),
          empCode: (f.empCode || "").trim(),
          empName: (f.empName || "").trim(),
          paymode: normalizeSelect(f.paymode),
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load PF list.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "List load failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only refetch on page change after meta is loaded and filters are valid
  useEffect(() => {
    if (!metaLoaded) return;
    if (year === "Select" || month === "Select") return;

    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ---------- Autocomplete ----------
  useEffect(() => {
    const run = async () => {
      const q = empCode.trim();
      if (!q || q.length < 1) {
        setCodeSuggestions([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/employee-pf-list/autocomplete/emp-code`, {
          params: {
            finYearId: Number(finYearId),
            branchId: normalizeSelect(branch),
            branchType: String(branchType),
            q,
          },
        });
        if (res?.data?.success) {
          setCodeSuggestions(res.data.items || []);
        }
      } catch {
        // silent
      }
    };

    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empCode, branch]);

  useEffect(() => {
    const run = async () => {
      const q = empName.trim();
      if (!q || q.length < 1) {
        setNameSuggestions([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/employee-pf-list/autocomplete/emp-name`, {
          params: {
            finYearId: Number(finYearId),
            branchId: normalizeSelect(branch),
            branchType: String(branchType),
            q,
          },
        });
        if (res?.data?.success) {
          setNameSuggestions(res.data.items || []);
        }
      } catch {
        // silent
      }
    };

    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empName, branch]);

  // ---------- Actions ----------
  const handleSearch = async (e) => {
    e?.preventDefault?.();

    // ASP validation: warns ONLY if all 3 are Select
    const allSelect =
      normalizeSelect(branch) === "Select" &&
      (monthNameByValue[normalizeSelect(month)] || "Select") === "Select" &&
      normalizeSelect(year) === "Select";

    if (allSelect) {
      toast.warn("Select Mandatory Fields");
      return;
    }

    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await fetchList(page);
  };

  const handleExcel = () => {
    // enable only if total > 0 (ASP enables btnExcel only when rows exist)
    if (total <= 0) {
      toast.warn("No data to export.");
      return;
    }

    const monthName = monthNameByValue[normalizeSelect(month)] || "Select";

    const url =
      `${BASE_URL}/api/employee-pf-list/excel?` +
      `finYearId=${encodeURIComponent(Number(finYearId))}` +
      `&branchId=${encodeURIComponent(normalizeSelect(branch))}` +
      `&monthName=${encodeURIComponent(monthName)}` +
      `&year=${encodeURIComponent(normalizeSelect(year))}` +
      `&departmentId=${encodeURIComponent(normalizeSelect(departmentId))}` +
      `&empCode=${encodeURIComponent(empCode.trim())}` +
      `&empName=${encodeURIComponent(empName.trim())}` +
      `&paymode=${encodeURIComponent(normalizeSelect(paymode))}`;

    window.location.href = url;
  };

  const handlePrint = () => {
    const monthName = monthNameByValue[normalizeSelect(month)] || "Select";

    const qs =
      `finYearId=${encodeURIComponent(Number(finYearId))}` +
      `&branchId=${encodeURIComponent(normalizeSelect(branch))}` +
      `&monthName=${encodeURIComponent(monthName)}` +
      `&year=${encodeURIComponent(normalizeSelect(year))}` +
      `&departmentId=${encodeURIComponent(normalizeSelect(departmentId))}` +
      `&empCode=${encodeURIComponent(empCode.trim())}` +
      `&empName=${encodeURIComponent(empName.trim())}` +
      `&paymode=${encodeURIComponent(normalizeSelect(paymode))}`;

    window.open(`/PayRoll/EmployeePFListing_Print?${qs}`, "_blank");
  };

  // Pagination UI numbers
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  // Columns (match ASP grid)
  const columns = useMemo(
    () => [
      { key: "__sno", label: "S.No" },
      { key: "fld_pfuanno", label: "UAN No" },
      { key: "fld_pfno", label: "PF No" },
      { key: "fld_employeecode", label: "Employee Code" },
      { key: "fld_employee", label: "Name of Workman" },
      { key: "wages", label: "Basic Wages" },
      { key: "fld_absence", label: "Leave" },
      { key: "fld_lop", label: "LOP" },
      { key: "fld_paidbasic", label: "Paid Basic" },
      { key: "otherallowance", label: "Difference" },
      { key: "totalwages", label: "Paid Gross" },
      { key: "fld_pf", label: "Pf 12%" },
      { key: "fld_pf8", label: "8.33%" },
      { key: "fld_pf3", label: "3.67%" },
      { key: "fld_BranchName", label: "Branch" },
    ],
    []
  );

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE PF LIST</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee PF List Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter • Search • Pagination • Excel • Print
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleExcel} className={subtleBtn} disabled={loading || total <= 0}>
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>

              <button onClick={handlePrint} className={subtleBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                <div>
                  <label className={labelBase}>
                    Year <span className="text-red-500">*</span>
                  </label>
                  <select value={year} onChange={(e) => setYear(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>
                    Month <span className="text-red-500">*</span>
                  </label>
                  <select value={month} onChange={(e) => setMonth(e.target.value)} className={inputBase}>
                    {monthOptions.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <select value={branch} onChange={(e) => setBranch(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Department</label>
                  <select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    className={inputBase}
                  >
                    <option value="Select">Select</option>
                    {departments.map((d) => (
                      <option key={d.fld_deptid} value={String(d.fld_deptid)}>
                        {d.fld_departmentname}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Employee Code with autocomplete */}
                <div className="relative">
                  <label className={labelBase}>Employee Code</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={empCode}
                      onChange={(e) => {
                        setEmpCode(e.target.value);
                        setCodeOpen(true);
                      }}
                      onBlur={() => setTimeout(() => setCodeOpen(false), 150)}
                      placeholder="Type employee code..."
                      className={inputBase}
                    />
                  </div>

                  {codeOpen && codeSuggestions.length > 0 && (
                    <div
                      className={`absolute z-30 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                        isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                      }`}
                    >
                      {codeSuggestions.slice(0, 10).map((item, idx) => (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => {
                            setEmpCode(item);
                            setCodeOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-900"
                          }`}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Employee Name with autocomplete */}
                <div className="relative">
                  <label className={labelBase}>Employee Name</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <input
                      value={empName}
                      onChange={(e) => {
                        setEmpName(e.target.value);
                        setNameOpen(true);
                      }}
                      onBlur={() => setTimeout(() => setNameOpen(false), 150)}
                      placeholder="Type employee name..."
                      className={inputBase}
                    />
                  </div>

                  {nameOpen && nameSuggestions.length > 0 && (
                    <div
                      className={`absolute z-30 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                        isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                      }`}
                    >
                      {nameSuggestions.slice(0, 10).map((item, idx) => (
                        <button
                          type="button"
                          key={idx}
                          onClick={() => {
                            setEmpName(item);
                            setNameOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            isDark ? "hover:bg-white/10 text-gray-200" : "hover:bg-gray-50 text-gray-900"
                          }`}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelBase}>Payment Mode</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Landmark className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <select value={paymode} onChange={(e) => setPaymode(e.target.value)} className={inputBase}>
                      <option value="Select">Select</option>
                      <option value="Cash">Cash</option>
                      <option value="Bank">Bank</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center mt-6 gap-3">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    {columns.map((c) => (
                      <th key={c.key} className="px-4 py-4 text-center font-bold whitespace-nowrap">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {columns.map((c) => (
                          <td key={c.key} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const flag = String(r.no ?? r.slno ?? "") === "1";
                      return (
                        <tr
                          key={idx}
                          className={`${
                            flag
                              ? "bg-yellow-200 text-black"
                              : isDark
                              ? "bg-gray-900 text-gray-200 hover:bg-gray-800"
                              : "bg-white text-gray-900 hover:bg-gray-50"
                          }`}
                        >
                          {columns.map((c) => {
                            if (c.key === "__sno") {
                              return (
                                <td key={c.key} className="px-4 py-4 text-sm text-center whitespace-nowrap">
                                  {(page - 1) * pageSize + idx + 1}
                                </td>
                              );
                            }
                            return (
                              <td key={c.key} className="px-4 py-4 text-sm text-center whitespace-nowrap">
                                {r?.[c.key] ?? ""}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeePFList;